import React, { createContext, useContext, useState, useMemo, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { ViewType } from "../../../constants/enums";
import { RecommendationProjectTypeEnum } from "../../../types";
import { enumFromStringValue } from "../../../utils/enumFromStringValue";
import { useHomePageData } from "../hooks/useHomePageData";
import { useHomePageActions } from "../hooks/useHomePageActions";
import { HomePageContextValue, UIState, HomePageActions } from "./types";

const HomePageContext = createContext<HomePageContextValue | undefined>(undefined);

interface HomePageProviderProps {
  readonly children: ReactNode;
}

/**
 * HomePageProvider - Centralized state management for HomePage
 * Following Clean Code principle: Single Responsibility - manages all HomePage state
 */
export const HomePageProvider: React.FC<HomePageProviderProps> = ({ children }) => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  // Extract current view from URL params
  const currentViewType: ViewType =
    enumFromStringValue(ViewType, searchParams.get("view") ?? "") ?? ViewType.LIST;

  // Get data from existing hooks
  const {
    forestationProjects,
    carbonProjects,
    recommendedProjects,
    favoriteProjects,
    pagination,
    search,
    selectedProject,
    selectedRecommendation,
    totalCounts,
  } = useHomePageData();

  const { handleSearchChange, handlePageChange } = useHomePageActions();

  // UI State following Clean Code principle: Group related data
  const uiState: UIState = useMemo(
    () => ({
      currentViewType,
      selectedProject,
      selectedRecommendation,
      searchValue: search,
      pagination: {
        page: pagination.page,
        perPage: pagination.perPage,
      },
      isFilterDrawerOpen,
    }),
    [
      currentViewType,
      selectedProject,
      selectedRecommendation,
      search,
      pagination.page,
      pagination.perPage,
      isFilterDrawerOpen,
    ],
  );

  // Actions following Clean Code principle: Pure, focused functions
  const actions: HomePageActions = useMemo(
    () => ({
      handleSearchChange,
      handlePageChange,
      openFilterDrawer: () => setIsFilterDrawerOpen(true),
      closeFilterDrawer: () => setIsFilterDrawerOpen(false),
      setSelectedRecommendation: (type: RecommendationProjectTypeEnum) => {
        // This will need to be connected to the existing dispatch logic
        // For now, maintaining the existing behavior
      },
    }),
    [handleSearchChange, handlePageChange],
  );

  // Project data state following Clean Code principle: Immutable data structures
  const projectData = useMemo(
    () => ({
      carbonProjects: {
        items: carbonProjects.items,
        isLoading: carbonProjects.isLoading,
        error: carbonProjects.error,
      },
      recommendedProjects: {
        items: recommendedProjects.items,
        isLoading: recommendedProjects.isLoading,
        error: recommendedProjects.error,
        pagination: recommendedProjects.pagination,
      },
      forestationProjects: {
        items: forestationProjects.items,
        isLoading: forestationProjects.isLoading,
        total: forestationProjects.total,
      },
      favoriteProjects: {
        items: favoriteProjects.items,
      },
      totalCounts,
    }),
    [carbonProjects, recommendedProjects, forestationProjects, favoriteProjects, totalCounts],
  );

  // Computed values following Clean Code principle: Self-documenting names
  const computed = useMemo(() => {
    const hasApiErrors = !!recommendedProjects.error && !!carbonProjects.error;

    const currentRecommendedProjects =
      selectedRecommendation === RecommendationProjectTypeEnum.Forests
        ? forestationProjects.items
        : recommendedProjects.items;

    const isLoadingRecommended =
      selectedRecommendation === RecommendationProjectTypeEnum.Forests
        ? forestationProjects.isLoading
        : recommendedProjects.isLoading;

    return {
      hasApiErrors,
      currentRecommendedProjects,
      isLoadingRecommended,
    };
  }, [
    recommendedProjects.error,
    carbonProjects.error,
    selectedRecommendation,
    forestationProjects.items,
    forestationProjects.isLoading,
    recommendedProjects.items,
    recommendedProjects.isLoading,
  ]);

  const contextValue: HomePageContextValue = useMemo(
    () => ({
      projectData,
      uiState,
      actions,
      computed,
    }),
    [projectData, uiState, actions, computed],
  );

  return <HomePageContext.Provider value={contextValue}>{children}</HomePageContext.Provider>;
};

/**
 * Custom hook to access HomePage context
 * Following Clean Code principle: Self-documenting function name
 */
export const useHomePageContext = (): HomePageContextValue => {
  const context = useContext(HomePageContext);

  if (context === undefined) {
    throw new Error("useHomePageContext must be used within a HomePageProvider");
  }

  return context;
};
